// Phase 4D.3A — server functions for the executable tool layer.
//
// Reads of public governed content are open (same authority as the site).
// Anything user-owned requires the caller's bearer token and runs under RLS.
// Writes are two-step: plan (never writes) then confirm (single-use token).

import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import {
  EXECUTABLE_READ_TOOLS,
  EXECUTABLE_WRITE_TOOLS,
  type ToolArgs,
} from "@/lib/intelligence/tools/broker";

const readTool = z.enum(EXECUTABLE_READ_TOOLS);
const writeTool = z.enum(EXECUTABLE_WRITE_TOOLS);

const writeArgs = z
  .object({
    enabled: z.boolean().optional(),
    text: z.string().max(500).optional(),
    memoryId: z.string().uuid().optional(),
  })
  .strict();

function authHeader(): string | null {
  // Imported lazily so this module stays client-bundle safe.
  return null;
}

/** Public governed reads. No auth, no user data, no secrets. */
export const runPublicReadTool = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) =>
    z
      .object({
        tool: readTool.exclude(["account_read"]),
        query: z.string().max(200).default(""),
      })
      .parse(raw),
  )
  .handler(async ({ data }) => {
    const { executeReadTool } = await import("@/lib/intelligence/tools/execute.server");
    return executeReadTool(
      data.tool,
      { query: data.query } as ToolArgs,
      { authorization: authHeader(), userId: null },
    );
  });

/** Multi-pass governed research across site content. Zero external cost. */
export const runSiteResearchLane = createServerFn({ method: "POST" })
  .inputValidator((raw: unknown) => z.object({ query: z.string().max(200) }).parse(raw))
  .handler(async ({ data }) => {
    const { runSiteResearch, productionResearchRetrieve } = await import(
      "@/lib/intelligence/research/site-research.server"
    );
    const report = await runSiteResearch(data.query, { retrieve: productionResearchRetrieve() });
    // The prompt block never leaves the server: only sources + safe trace do.
    return { sources: report.sources, trace: report.trace };
  });

/** The caller's own account state, read under their RLS. */
export const runAccountRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const { executeReadTool } = await import("@/lib/intelligence/tools/execute.server");
    return executeReadTool(
      "account_read",
      {},
      {
        authorization: getRequest()?.headers.get("authorization") ?? null,
        userId: context.userId,
      },
    );
  });

/** Step 1: plan a first-party write. NEVER writes. Returns a confirmation token. */
export const planIntelligenceWrite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z.object({ tool: writeTool, args: writeArgs }).parse(raw),
  )
  .handler(async ({ data, context }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const { planWriteTool } = await import("@/lib/intelligence/tools/execute.server");
    return planWriteTool(data.tool, data.args as ToolArgs, {
      authorization: getRequest()?.headers.get("authorization") ?? null,
      userId: context.userId,
    });
  });

/** Step 2: explicit confirmation. Single-use, argument-bound, short expiry. */
export const confirmIntelligenceWrite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((raw: unknown) =>
    z
      .object({
        tool: writeTool,
        args: writeArgs,
        confirmationToken: z.string().min(20).max(2000),
      })
      .parse(raw),
  )
  .handler(async ({ data, context }) => {
    const { getRequest } = await import("@tanstack/react-start/server");
    const { executeWriteTool } = await import("@/lib/intelligence/tools/execute.server");
    return executeWriteTool(data.tool, data.args as ToolArgs, data.confirmationToken, {
      authorization: getRequest()?.headers.get("authorization") ?? null,
      userId: context.userId,
    });
  });
